import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { SiteProvider } from '@/context/SiteContext';

import PublicLayout from '@/components/layout/PublicLayout';
import AdminLayout from '@/components/admin/AdminLayout';

// Public Pages
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import ServicesPage from '@/pages/ServicesPage';
import CSRProjectsPage from '@/pages/CSRProjectsPage';
import CSREligibilityDocumentsPage from '@/pages/csr/CSREligibilityDocumentsPage';
import OurCSRProjectsPage from '@/pages/csr/OurCSRProjectsPage';
import CSRProposalProjectsPage from '@/pages/csr/CSRProposalProjectsPage';
import CSRProjectProposalsPage from '@/pages/csr/CSRProjectProposalsPage';
import CSRGuidelinesMCAPage from '@/pages/csr/CSRGuidelinesMCAPage';
import JobPortalPage from '@/pages/JobPortalPage';
import SectorAchievementsPage from '@/pages/SectorAchievementsPage';
import SuccessStoriesPage from '@/pages/SuccessStoriesPage';
import GalleryPage from '@/pages/GalleryPage';
import VolunteerGalleryPage from '@/pages/VolunteerGalleryPage';
import MediaAwardsPage from '@/pages/MediaAwardsPage';
import VolunteerPage from '@/pages/VolunteerPage';
import DownloadsPage from '@/pages/DownloadsPage';
import DonatePage from '@/pages/DonatePage';
import ContactPage from '@/pages/ContactPage';
import PartnersPage from '@/pages/PartnersPage';
import NotFoundPage from '@/pages/NotFoundPage';

// Admin Pages
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminServicesPage from '@/pages/admin/AdminServicesPage';
import AdminServiceYearStatsPage from '@/pages/admin/AdminServiceYearStatsPage';
import AdminProjectsPage from '@/pages/admin/AdminProjectsPage';
import AdminProposalProjectsPage from '@/pages/admin/AdminProposalProjectsPage';
import AdminCSRProjectPDFsPage from '@/pages/admin/AdminCSRProjectPDFsPage';
import AdminSectorAchievementsPage from '@/pages/admin/AdminSectorAchievementsPage';
import AdminVolunteerAlbumsPage from '@/pages/admin/AdminVolunteerAlbumsPage';
import AdminAdvisorsPage from '@/pages/admin/AdminAdvisorsPage';
import AdminSectorsPage from '@/pages/admin/AdminSectorsPage';
import AdminAssociationsPage from '@/pages/admin/AdminAssociationsPage';
import AdminContentBlocksPage from '@/pages/admin/AdminContentBlocksPage';
import AdminNewsPage from '@/pages/admin/AdminNewsPage';
import AdminTestimonialsPage from '@/pages/admin/AdminTestimonialsPage';
import AdminGalleryPage from '@/pages/admin/AdminGalleryPage';
import AdminGalleryAlbumsPage from '@/pages/admin/AdminGalleryAlbumsPage';
import AdminPartnersPage from '@/pages/admin/AdminPartnersPage';
import AdminDownloadsPage from '@/pages/admin/AdminDownloadsPage';
import AdminImpactStatsPage from '@/pages/admin/AdminImpactStatsPage';
import AdminMediaAwardsPage from '@/pages/admin/AdminMediaAwardsPage';
import AdminVolunteersPage from '@/pages/admin/AdminVolunteersPage';
import AdminContactsPage from '@/pages/admin/AdminContactsPage';
import AdminDonationsPage from '@/pages/admin/AdminDonationsPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';

const RequireAuth = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;
    return children;
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SiteProvider>
                        <Routes>
                            {/* Public routes */}
                            <Route path="/" element={<PublicLayout />}>
                                <Route index element={<HomePage />} />
                                <Route path="about" element={<AboutPage />} />
                                <Route path="services" element={<ServicesPage />} />
                                <Route path="sector-achievements" element={<SectorAchievementsPage />} />
                                {/* CSR Projects — hub landing + 5 sub-pages */}
                                <Route path="csr-projects" element={<CSRProjectsPage />} />
                                <Route
                                    path="csr/eligibility-documents"
                                    element={<CSREligibilityDocumentsPage />}
                                />
                                <Route
                                    path="csr/our-projects"
                                    element={<OurCSRProjectsPage />}
                                />
                                <Route
                                    path="csr/proposal-projects"
                                    element={<CSRProposalProjectsPage />}
                                />
                                <Route
                                    path="csr/project-proposals"
                                    element={<CSRProjectProposalsPage />}
                                />
                                <Route
                                    path="csr/guidelines-mca"
                                    element={<CSRGuidelinesMCAPage />}
                                />
                                <Route
                                    path="csr-eligibility"
                                    element={<Navigate to="/csr/eligibility-documents" replace />}
                                />
                                <Route
                                    path="proposal-projects"
                                    element={<Navigate to="/csr/proposal-projects" replace />}
                                />
                                <Route path="job-portal" element={<JobPortalPage />} />
                                <Route
                                    path="success-stories"
                                    element={<SuccessStoriesPage />}
                                />
                                <Route path="gallery" element={<GalleryPage />} />
                                <Route
                                    path="volunteer-gallery"
                                    element={<VolunteerGalleryPage />}
                                />
                                <Route
                                    path="media-awards"
                                    element={<MediaAwardsPage />}
                                />
                                <Route path="volunteer" element={<VolunteerPage />} />
                                <Route path="downloads" element={<DownloadsPage />} />
                                <Route path="donate" element={<DonatePage />} />
                                <Route path="contact" element={<ContactPage />} />
                                <Route path="partners" element={<PartnersPage />} />
                                <Route path="*" element={<NotFoundPage />} />
                            </Route>

                            {/* Admin routes */}
                            <Route path="/admin/login" element={<AdminLoginPage />} />
                            <Route
                                path="/admin"
                                element={
                                    <RequireAuth>
                                        <AdminLayout />
                                    </RequireAuth>
                                }
                            >
                                <Route index element={<AdminDashboardPage />} />
                                <Route path="services" element={<AdminServicesPage />} />
                                <Route
                                    path="service-year-stats"
                                    element={<AdminServiceYearStatsPage />}
                                />
                                <Route path="projects" element={<AdminProjectsPage />} />
                                <Route
                                    path="proposal-projects"
                                    element={<AdminProposalProjectsPage />}
                                />
                                <Route
                                    path="csr-project-pdfs"
                                    element={<AdminCSRProjectPDFsPage />}
                                />
                                <Route
                                    path="sector-achievements"
                                    element={<AdminSectorAchievementsPage />}
                                />
                                <Route
                                    path="volunteer-albums"
                                    element={<AdminVolunteerAlbumsPage />}
                                />
                                <Route path="advisors" element={<AdminAdvisorsPage />} />
                                <Route path="sectors" element={<AdminSectorsPage />} />
                                <Route path="associations" element={<AdminAssociationsPage />} />
                                <Route
                                    path="content-blocks"
                                    element={<AdminContentBlocksPage />}
                                />
                                <Route path="news" element={<AdminNewsPage />} />
                                <Route
                                    path="testimonials"
                                    element={<AdminTestimonialsPage />}
                                />
                                <Route path="gallery" element={<AdminGalleryPage />} />
                                <Route
                                    path="gallery-albums"
                                    element={<AdminGalleryAlbumsPage />}
                                />
                                <Route path="partners" element={<AdminPartnersPage />} />
                                <Route path="downloads" element={<AdminDownloadsPage />} />
                                <Route
                                    path="impact-stats"
                                    element={<AdminImpactStatsPage />}
                                />
                                <Route
                                    path="media-awards"
                                    element={<AdminMediaAwardsPage />}
                                />
                                <Route
                                    path="volunteers"
                                    element={<AdminVolunteersPage />}
                                />
                                <Route path="contacts" element={<AdminContactsPage />} />
                                <Route path="donations" element={<AdminDonationsPage />} />
                                <Route path="settings" element={<AdminSettingsPage />} />
                            </Route>
                        </Routes>
                        <Toaster richColors position="top-right" />
                    </SiteProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
